// Markdown-lite renderer for model-generated text (deal briefs, Knowledge
// answers, theme summaries, enrichment blurbs). Supports **bold**, *italic*,
// `code`, [links](https://…), bullet ("- ") and numbered ("1. ") lists, and
// #–#### headings. Everything is emitted as real React elements — strings
// stay strings, so nothing in the text can inject HTML, and links are limited
// to http(s) URLs.

import { Fragment } from "react";

const TOKEN =
  /(\*\*[^*\n]+\*\*|`[^`\n]+`|\[[^\]\n]+\]\(https?:\/\/[^)\s]+\)|\*[^*\s][^*\n]*\*)/g;
const LINK = /^\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)$/;

/** Inline markdown → React nodes. */
function spans(line: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(line))) {
    if (m.index > last) out.push(line.slice(last, m.index));
    const t = m[0];
    const link = LINK.exec(t);
    if (t.startsWith("**")) {
      out.push(
        <strong key={m.index} style={{ color: "var(--ink)", fontWeight: 600 }}>
          {t.slice(2, -2)}
        </strong>,
      );
    } else if (t.startsWith("`")) {
      out.push(
        <code key={m.index} style={{ fontSize: "0.92em", background: "var(--raised)", padding: "1px 4px", borderRadius: 4 }}>
          {t.slice(1, -1)}
        </code>,
      );
    } else if (link) {
      out.push(
        <a key={m.index} href={link[2]} target="_blank" rel="noopener noreferrer"
          style={{ color: "var(--accent)" }}>
          {link[1]}
        </a>,
      );
    } else {
      out.push(<em key={m.index}>{t.slice(1, -1)}</em>);
    }
    last = m.index + t.length;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^#{1,4}\s+(.*)$/;

/** Markdown-lite text rendered safely (no innerHTML). */
export function Md({ text }: { text: string }) {
  const blocks: React.ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let prevInline = false;

  const flushList = (key: number) => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    blocks.push(
      <Tag key={`l${key}`} style={{ margin: "6px 0", paddingLeft: 20 }}>
        {list.items.map((it, j) => (
          <li key={j} style={{ margin: "2px 0" }}>{spans(it)}</li>
        ))}
      </Tag>,
    );
    list = null;
  };

  text.split("\n").forEach((line, i) => {
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      if (!list || list.ordered !== ordered) { flushList(i); list = { ordered, items: [] }; }
      list.items.push((bullet ?? numbered)![1]);
      prevInline = false;
      return;
    }
    flushList(i);
    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push(
        <div key={i} style={{ fontWeight: 600, color: "var(--ink)", margin: "10px 0 4px" }}>
          {spans(heading[1])}
        </div>,
      );
      prevInline = false;
      return;
    }
    blocks.push(
      <Fragment key={i}>
        {prevInline && <br />}
        {spans(line)}
      </Fragment>,
    );
    prevInline = true;
  });
  flushList(text.length);

  return <>{blocks}</>;
}
